import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ShieldAlert, CheckCircle, ShieldBan } from 'lucide-react'
import type { QuarantineEmailData } from '../../../shared/types/chat'

interface QuarantineCardProps {
  email: QuarantineEmailData
  onRelease: () => void
  onBlock: () => void
}

export default function QuarantineCard({ email, onRelease, onBlock }: QuarantineCardProps) {
  const { t } = useTranslation('chat')
  const [resolved, setResolved] = useState<'released' | 'blocked' | null>(null)

  const handleRelease = () => {
    setResolved('released')
    onRelease()
  }

  const handleBlock = () => {
    setResolved('blocked')
    onBlock()
  }

  return (
    <div className="rounded-xl border border-amber/20 bg-amber/5 px-3 py-3 space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2">
        <ShieldAlert size={14} className="text-amber/70 flex-shrink-0" />
        <span className="text-sm text-amber/90 font-medium">{t('quarantine.header')}</span>
        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-amber/15 text-amber/70">
          {email.reason}
        </span>
      </div>

      {/* Sender & subject */}
      <div className="pl-[22px] space-y-0.5">
        <p className="text-xs text-pearl/50">
          {t('email.from')} {email.sender} &lt;{email.senderEmail}&gt;
        </p>
        <p className="text-sm text-ivory/80 truncate">{email.subject}</p>
      </div>

      {/* Actions */}
      {resolved ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-1.5 pl-[22px]"
        >
          {resolved === 'released' ? (
            <>
              <CheckCircle size={12} className="text-emerald/70" />
              <span className="text-xs text-emerald/70">{t('quarantine.released')}</span>
            </>
          ) : (
            <>
              <ShieldBan size={12} className="text-rose/70" />
              <span className="text-xs text-rose/70">{t('quarantine.blocked')}</span>
            </>
          )}
        </motion.div>
      ) : (
        <div className="flex gap-2 pl-[22px]">
          <button
            onClick={handleRelease}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald/30 text-emerald/80 text-xs hover:bg-emerald/10 transition-colors"
          >
            <CheckCircle size={12} />
            {t('quarantine.release')}
          </button>
          <button
            onClick={handleBlock}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose/30 text-rose/80 text-xs hover:bg-rose/10 transition-colors"
          >
            <ShieldBan size={12} />
            {t('quarantine.block')}
          </button>
        </div>
      )}
    </div>
  )
}
